import { useEffect, useRef } from "react";
import { getWorkspaceAccessToken, createGoogleSheet, initWorkspaceAuth } from "../lib/firebase";

export default function GoogleSheetBackgroundSync({ apiToken }: { apiToken: string }) {
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    let unsubscribe = () => {};
    // Ensure we track workspace auth correctly
    unsubscribe = initWorkspaceAuth(
      async (user, token) => {
        // Connected!
        // Start continuous sync
        if (!syncIntervalRef.current) {
          syncDataToSheet(token, apiToken); // Initial trigger
          syncIntervalRef.current = setInterval(() => {
            const currentToken = getWorkspaceAccessToken();
            if (currentToken) {
              syncDataToSheet(currentToken, apiToken);
            }
          }, 30000); // 30 seconds interval
        }
      },
      () => {
        // Disconnected
        if (syncIntervalRef.current) {
          clearInterval(syncIntervalRef.current);
          syncIntervalRef.current = null;
        }
      }
    );

    return () => {
      unsubscribe();
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [apiToken]);

  return null; // pure headless component
}

const syncDataToSheet = async (accessToken: string, apiToken: string) => {
  try {
    let sheetId = localStorage.getItem('SPREADSHEET_ID');

    // 1. If no sheet ID, create one
    if (!sheetId) {
      const result = await createGoogleSheet(accessToken, "IQ-Doctor");
      if (result.success && result.spreadsheetId) {
        sheetId = result.spreadsheetId;
        localStorage.setItem('SPREADSHEET_ID', sheetId);
        console.log("IQ-Doctor Google Sheet created with ID:", sheetId);
      } else {
        throw new Error(result.error);
      }
    }

    // 2. Fetch local data from the server
    const patientsRes = await fetch("/api/patients", { headers: { Authorization: `Bearer ${apiToken}` } });
    if (!patientsRes.ok) return;
    const patientsResData = await patientsRes.json();
    if (!patientsResData.success) return;

    const patients = patientsResData.patients || [];

    // Map patients to Google Sheet rows
    const rows = [
      ["Patient ID", "Name", "Gender", "Date of Birth", "Phone", "National ID", "Created At"] // Header Row
    ];

    patients.forEach((p: any) => {
      rows.push([
        p.id || "",
        p.name || "",
        p.gender || "",
        p.dob || "",
        p.phone || "",
        p.nationalId || "",
        p.createdAt || ""
      ]);
    });

    // 3. Get Metadata to find first sheet name
    const metaRes = await fetch(`https://sheets.googleapis.com/v4/spreadsheets/${sheetId}`, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    const meta = await metaRes.json();
    if (!meta.sheets || meta.sheets.length === 0) return;
    const sheetName = meta.sheets[0].properties.title;

    // 4. Clear existing values to prevent stale overlaps
    await fetch(`https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/'${sheetName}'!A:Z:clear`, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` }
    });

    // 5. Update cells
    const updateRes = await fetch(`https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/'${sheetName}'!A1?valueInputOption=USER_ENTERED`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        values: rows
      }),
    });

    if (!updateRes.ok) {
      const err = await updateRes.json();
      console.error("[Sheets Auto-Sync] Update failed.", err);
    } else {
      console.log("[Sheets Auto-Sync] Sync complete.");
    }

  } catch (e) {
    console.error("[Sheets Auto-Sync] Error during background synchronization:", e);
  }
};
